import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Loader2, TrendingUp, Car, Users, Star, CreditCard, Wallet } from "lucide-react";

const COLORS_PIE = ["#10B981", "#3B82F6", "#FACC15", "#EF4444", "#8B5CF6"];
const MONTH_LABELS = { "01": "Jan", "02": "Fev", "03": "Mar", "04": "Avr", "05": "Mai", "06": "Jun", "07": "Jul", "08": "Aou", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };
const STATUS_LABELS = { pending: "En attente", confirmed: "Confirme", in_progress: "En cours", completed: "Termine", cancelled: "Annule" };

const AXIS_TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const AXIS_TICK_BOLD = { fontSize: 12, fill: "#0A2540" };
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #CBD5E1", fontSize: 12 };

function formatMonth(m) {
  if (!m) return m;
  const parts = m.split("-");
  return parts.length === 2 ? `${MONTH_LABELS[parts[1]] || parts[1]} ${parts[0].slice(2)}` : m;
}

export default function DriverStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/stats");
      setStats(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Failed to fetch stats:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" />
    </div>
  );

  if (!stats) return <p className="text-center py-10 text-[#64748B]">Impossible de charger les statistiques</p>;

  const monthlyChart = (stats.monthly_chart || []).map(d => ({ ...d, month: formatMonth(d.month) }));
  const ratingChart = stats.rating_chart || [];
  const statusChart = (stats.status_chart || []).map(d => ({ ...d, status: STATUS_LABELS[d.status] || d.status }));

  return (
    <div className="space-y-6" data-testid="driver-stats">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<Wallet className="w-5 h-5 text-[#10B981]" />} label="Revenus nets" value={`${(stats.total_earnings || 0).toLocaleString()} XAF`} testid="stat-earnings" />
        <StatCard icon={<CreditCard className="w-5 h-5 text-[#EF4444]" />} label="Solde TFT" value={`${(stats.tft_balance || 0).toLocaleString()}`} testid="stat-tft" />
        <StatCard icon={<Users className="w-5 h-5 text-[#3B82F6]" />} label="Reservations" value={stats.total_bookings} testid="stat-bookings" />
        <StatCard icon={<Star className="w-5 h-5 text-[#FACC15]" />} label="Note moyenne" value={stats.rating ? `${stats.rating.toFixed(1)}/5` : "N/A"} testid="stat-rating" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<Car className="w-5 h-5 text-[#0A2540]" />} label="Trajets actifs" value={stats.active_rides} testid="stat-active-rides" />
        <StatCard icon={<Car className="w-5 h-5 text-[#64748B]" />} label="Total trajets" value={stats.total_rides} testid="stat-total-rides" />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-[#10B981]" />} label="Completees" value={stats.completed_bookings} testid="stat-completed" />
        <StatCard icon={<Users className="w-5 h-5 text-[#64748B]" />} label="Avis recus" value={stats.total_reviews} testid="stat-reviews" />
      </div>

      {/* Monthly Earnings Chart */}
      {monthlyChart.length > 0 && (
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="monthly-earnings-chart">
          <h3 className="text-sm font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Revenus mensuels (XAF)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyChart}>
              <XAxis dataKey="month" tick={AXIS_TICK_STYLE} />
              <YAxis tick={AXIS_TICK_STYLE} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => [`${v.toLocaleString()} XAF`, "Revenus"]} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="earnings" fill="#0A2540" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {/* Rating Distribution */}
        {ratingChart.length > 0 && (
          <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="rating-distribution-chart">
            <h3 className="text-sm font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Distribution des notes</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ratingChart} layout="vertical">
                <XAxis type="number" tick={AXIS_TICK_STYLE} />
                <YAxis dataKey="stars" type="category" tick={AXIS_TICK_BOLD} width={30} tickFormatter={v => `${v}`} />
                <Tooltip formatter={(v) => [v, "Avis"]} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#FACC15" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Booking Status Pie */}
        {statusChart.length > 0 && (
          <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="booking-status-chart">
            <h3 className="text-sm font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Statut des reservations</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusChart} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={70} labelLine={false}
                  label={({ status, percent }) => percent > 0.05 ? `${status} ${(percent*100).toFixed(0)}%` : ""} >
                  {statusChart.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                </Pie>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-[#64748B]">{label}</span></div>
      <p className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{value}</p>
    </div>
  );
}
